
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, ChevronDown, ChevronUp } from "lucide-react";
import { TutorialStep } from "@/types/tutorial";
import CollapsibleStep from "./CollapsibleStep";

interface StepManagerProps {
  steps: TutorialStep[];
  onChange: (steps: TutorialStep[]) => void;
}

const StepManager: React.FC<StepManagerProps> = ({ steps, onChange }) => {
  const [allExpanded, setAllExpanded] = useState(true);

  const addStep = () => {
    const newStep: TutorialStep = {
      id: `step-${Date.now()}`,
      text: "",
      images: [],
      completed: false
    };
    onChange([...steps, newStep]);
    
    // Auto-focus the new step after a brief delay
    setTimeout(() => {
      const newStepElement = document.querySelector(`[data-step-id="${newStep.id}"] textarea`);
      if (newStepElement) {
        (newStepElement as HTMLElement).focus();
        newStepElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const updateStep = (id: string, updates: Partial<TutorialStep>) => {
    onChange(steps.map(step => 
      step.id === id ? { ...step, ...updates } : step
    ));
  };

  const removeStep = (id: string) => {
    onChange(steps.filter(step => step.id !== id));
  };

  const addImageToStep = (stepId: string, imageUrl: string) => {
    if (!imageUrl.trim()) return;
    
    const step = steps.find(s => s.id === stepId);
    if (step) {
      updateStep(stepId, { images: [...step.images, imageUrl.trim()] });
    }
  };

  const removeImageFromStep = (stepId: string, imageIndex: number) => {
    const step = steps.find(s => s.id === stepId);
    if (step) {
      const newImages = step.images.filter((_, index) => index !== imageIndex);
      updateStep(stepId, { images: newImages });
    }
  };

  const toggleAllSteps = () => {
    setAllExpanded(!allExpanded);
    // Trigger collapse/expand on all steps
    const stepElements = document.querySelectorAll('[data-step-collapsible]');
    stepElements.forEach(element => {
      const button = element.querySelector('button[data-collapse-trigger]');
      if (button) {
        (button as HTMLButtonElement).click();
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label className="text-sm font-medium">Steps ({steps.length})</Label>
        {steps.length > 0 && (
          <Button variant="outline" size="sm" onClick={toggleAllSteps}>
            {allExpanded ? <ChevronUp size={14} className="mr-1" /> : <ChevronDown size={14} className="mr-1" />}
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>
        )}
      </div>
      
      <div className="space-y-3">
        {steps.map((step, index) => (
          <CollapsibleStep
            key={step.id}
            step={step}
            stepIndex={index}
            onUpdate={(updates) => updateStep(step.id, updates)}
            onRemove={() => removeStep(step.id)}
            onAddImage={(imageUrl) => addImageToStep(step.id, imageUrl)}
            onRemoveImage={(imageIndex) => removeImageFromStep(step.id, imageIndex)}
            allExpanded={allExpanded}
          />
        ))}
      </div>
      
      <div className="sticky bottom-4 z-10 flex justify-center">
        <Button variant="outline" size="sm" onClick={addStep} className="bg-background shadow-lg border-2">
          <Plus size={14} className="mr-1" />
          Add Step
        </Button>
      </div>
      
      {steps.length === 0 && (
        <div className="text-center py-4 text-muted-foreground text-sm">
          No steps yet. Click "Add Step" to get started.
        </div>
      )}
    </div>
  );
};

export default StepManager;
